import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { ISubscriptionPlanDto } from '../Dtos/ClientDashboard/SubscriptionPlanDto';
import { isAfter, isBefore, subDays, subMonths } from 'date-fns';
import { DEFAULT_HEADERS, to_API_URL } from '../Services/RestService';


const FILTERS = {
    ACTIVE: (expiredAt: Date) => isAfter(expiredAt, new Date()),
    EXPIRED: (expiredAt: Date) => isBefore(expiredAt, new Date()),
    EXPIRED_LAST_MONTH: (expiredAt: Date) => isBefore(expiredAt, subMonths(new Date(), 1)),
    ALL: () => true,
    LAST_15_DAYS: (startedAt: Date) => isAfter(startedAt, subDays(new Date(), 15)),
    LAST_30_DAYS: (startedAt: Date) => isAfter(startedAt, subDays(new Date(), 30))
}
enum TIME_FILTERS {
    ACTIVE,
    EXPIRED,
    EXPIRED_LAST_MONTH,
    ALL,
    STARTED_LAST_15_DAYS,
    STARTED_LAST_30_DAYS
}

// Interfaces for DTOs
interface IStorageQuotaDto {
    id: number;
    totalAllowedStorageInMB: number;
    totalUsedStorageInMB: number;
    attributedAt: Date;
    expiresAt: Date;
}

interface IReceiptDto {
    id: number;
}

interface ISubscriptionType {
    id: number;
    title?: string;
    durationInDays?: number;
}

interface IPaymentDto {
    id: number;
    status: string;
    paymentMethod: string;
    receipt: IReceiptDto;
    subscriptionType: ISubscriptionType;
    priceInCents: number;
    currency: string;
    externalPaymentSession: string;
    paymentProvider: string;
    externalId: string;
    message: string;
    paymentSession: string;
    createdAt: number;
    paidAt: number;
    userId: number;
}

interface ISubscriptionTypeDto {
    id: number;
    title: string;
    priceInCents: number;
    predefinedQuota: IStorageQuotaDto;
    currency: string;
    durationInDays: number;
}

interface DashboardSubscriptionPlanDto {
    id: number;
    subscriptionType: ISubscriptionTypeDto;
    expiresAt: Date;
    payment: IPaymentDto;
    storageQuota: IStorageQuotaDto;
    email: string;
}

// Payment Info Component
const PaymentInfo = ({ payment }) => {
    const formatCurrency = (amountInCents, currency) => {
        const amount = amountInCents / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD'
        }).format(amount);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Pending';

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(new Date(timestamp));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PAID': return 'bg-green-100 text-green-800';
            case 'PENDING': return 'bg-yellow-100 text-yellow-800';
            case 'FAILED': return 'bg-red-100 text-red-800';
            case 'CANCELLED': return 'bg-gray-100 text-gray-800';
            default: return 'bg-blue-100 text-blue-800';
        }
    };

    return (
        <div className="bg-gray-50 p-3 rounded-md">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                    <span className="font-medium mr-2">Payment Provider:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                        {payment.paymentProvider}
                    </span>
                </div>
                <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(payment.status)}`}>
                    {payment.status}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-2">
                <div>
                    <span className="text-gray-600">Method:</span>
                    <span className="ml-1 font-medium">{payment.paymentMethod}</span>
                </div>
                <div>
                    <span className="text-gray-600">Receipt ID:</span>
                    <span className="ml-1 font-medium">#{payment.receipt.id}</span>
                </div>
                <div>
                    <span className="text-gray-600">Amount:</span>
                    <span className="ml-1 font-medium">{formatCurrency(payment.priceInCents, payment.currency)}</span>
                </div>
                <div>
                    <span className="text-gray-600">User ID:</span>
                    <span className="ml-1 font-medium">{payment.userId}</span>
                </div>
            </div>

            <div className="text-sm border-t border-gray-200 pt-2 mt-2">
                <div className="mb-1">
                    <span className="text-gray-600">Created:</span>
                    <span className="ml-1 font-medium">{formatTimestamp(payment.createdAt)}</span>
                </div>

                {payment.paidAt ? (
                    <div>
                        <span className="text-gray-600">Paid:</span>
                        <span className="ml-1 font-medium">{formatTimestamp(payment.paidAt)}</span>
                    </div>
                ) : (
                    <div className="text-yellow-600">Payment pending</div>
                )}
            </div>

            <div className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-2">
                <div>
                    <span>External ID: </span>
                    <span className="font-mono">{payment.externalId}</span>
                </div>
                <div>
                    <span>Session: </span>
                    <span className="font-mono">{payment.paymentSession}</span>
                </div>
            </div>

            {payment.message && (
                <div className="mt-2 text-sm bg-blue-50 p-2 rounded italic text-blue-800">
                    {payment.message}
                </div>
            )}
        </div>
    );
};

// Main component
export default function SubscriptionPlansDemo() {
    // Sample data for the demonstration
    const [cookies] = useCookies();
    const [viewablePlans, setViewable] = useState<ISubscriptionPlanDto[]>([]);
    const [subscriptionPlans, setSubscriptionPlans] = useState<ISubscriptionPlanDto[]>([]);
    const [showExpired, toggleExpired] = useState(false);
    const [filter, setFilter] = useState<TIME_FILTERS>(TIME_FILTERS.ACTIVE);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [lastUpdated] = useState(new Date());

    useEffect(() => {
        switch (filter) {
            case TIME_FILTERS.ACTIVE:
                setViewable(subscriptionPlans);
                break;
            case TIME_FILTERS.EXPIRED:
                setViewable(subscriptionPlans.filter((p) => FILTERS.EXPIRED(p.expiresAt)));
                break;
            case TIME_FILTERS.EXPIRED_LAST_MONTH:
                setViewable(subscriptionPlans.filter((p) => FILTERS.EXPIRED(p.expiresAt)))
                break;
            case TIME_FILTERS.ALL:
                setViewable(subscriptionPlans.filter((p) => FILTERS.EXPIRED(p.expiresAt)))
                break;
            case TIME_FILTERS.STARTED_LAST_15_DAYS:
                setViewable(subscriptionPlans.filter((p) => FILTERS.EXPIRED(p.expiresAt)))
                break;
            case TIME_FILTERS.STARTED_LAST_30_DAYS:
                setViewable(subscriptionPlans.filter((p) => FILTERS.EXPIRED(p.expiresAt)))
                break;
        }
    }, [filter, subscriptionPlans])

    useEffect(() => {
        getSubscriptions();
    }, [])

    async function getSubscriptions() {
        fetch(to_API_URL('api/v1/dashboard/subscriptions'), {
            method: "GET",
            headers: {
                ...DEFAULT_HEADERS,
                "Authorization": "Bearer " + cookies.accessToken,
            }
        }).then(async result => {
            if (result.ok) {
                const data: ISubscriptionPlanDto[] = await result.json();
                console.log("data", data[0]);
                setSubscriptionPlans(data);
            }
        })
            .catch(err => {
                console.log("failed to get subscriptions", err);
            })
            .finally(() => {
                // loading
            })
    }

    // Format date display with hours, minutes, and seconds
    const formatDate = (date) => {
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(new Date(date));
    };

    // Calculate storage usage percentage
    const calculateStoragePercentage = (quota) => {
        if (quota.totalAllowedStorageInMB === 0) return 0;
        return Math.min(100, Math.round((quota.totalUsedStorageInMB / quota.totalAllowedStorageInMB) * 100));
    };

    // Handle plan selection
    const handlePlanClick = (plan) => {
        setSelectedPlanId(plan.id === selectedPlanId ? null : plan.id);
    };

    // Format currency display
    const formatCurrency = (amountInCents, currency) => {
        const amount = amountInCents / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD'
        }).format(amount);
    };

    return (
        <div style={{ width: "60%" }} className="w-2/4  mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-blue-600 text-white p-4">
                <h2 className="text-xl font-semibold">Subscription Plans</h2>
                <div className="text-sm text-blue-100 mt-1">
                    Showing {subscriptionPlans.length} active subscriptions
                </div>
            </div>

            <ul className="divide-y divide-gray-200">
                {viewablePlans
                    .map(plan => (
                        <li
                            key={plan.id}
                            className={`p-4 cursor-pointer transition-colors duration-200 ${selectedPlanId === plan.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                            onClick={() => handlePlanClick(plan)}
                        >
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium text-gray-900">{plan.subscriptionType.title}</h3>
                                <span className="text-lg font-semibold text-blue-600">
                                    {formatCurrency(plan.subscriptionType.priceInCents, plan.subscriptionType.currency)}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                                <div>
                                    <span className="font-medium">Expires:</span>{' '}
                                    <span>{formatDate(plan.expiresAt)}</span>
                                </div>

                                <div>
                                    <span className="font-medium">Duration:</span>{' '}
                                    <span>{plan.subscriptionType.durationInDays} days</span>
                                </div>

                                <div className="col-span-2 flex items-center space-x-1 py-1 px-2 bg-gray-100 rounded mt-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <span className="font-medium">User:</span>{' '}
                                        <span className="text-blue-600">{plan.owner.email}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedPlanId === plan.id && (
                                <div className="mb-4">
                                    <PaymentInfo payment={plan.payment} />
                                </div>
                            )}

                            <div className="mt-3">
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="font-medium">Storage Usage:</span>
                                    <span>
                                        {plan.storageQuota.totalUsedStorageInMB.toFixed(1)} MB / {plan.storageQuota.totalAllowedStorageInMB.toFixed(1)} MB
                                    </span>
                                </div>

                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className={`h-2.5 rounded-full ${calculateStoragePercentage(plan.storageQuota) > 90 ? 'bg-red-500' : calculateStoragePercentage(plan.storageQuota) > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                        style={{ width: `${calculateStoragePercentage(plan.storageQuota)}%` }}
                                    ></div>
                                </div>
                            </div>
                        </li>
                    ))}
            </ul>

            <div className="bg-gray-50 px-4 py-3 text-xs text-gray-500 flex justify-between items-center">
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                <button
                    className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded text-xs transition-colors duration-200"
                >
                    Refresh Now
                </button>
            </div>
        </div>
    );
}